import axios, { AxiosError, AxiosResponse } from 'axios';
import React, { createContext, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useQueryClient } from 'react-query';
import inMemoryJWT from './inMemoryJwt';
import AuthResponseModel from '../Interfaces/AuthResponseModel';
import CurrentUserModel from '../Interfaces/CurrentUserModel';
import AuthContextModel, { AuthVariablesType } from '../Interfaces/AuthContextModel';
import GenericStringObject from '../Interfaces/GenericStringObject';

const localStorageKeys = ['remember', 'username', 'email'];
const refreshRedeemGuardCookie = 'refreshTokenRedeemGuard';
const refreshRedeemWindowMs = 90 * 1000; // 90 sec window
const refreshRedeemLimit = 3; // allow up to 3 refresh failures within window

interface RefreshRedeemGuardClientData {
    count: number;
    expiresAt: number;
}

interface RefreshRedeemGuardState {
    data: Record<string, RefreshRedeemGuardClientData>;
}

interface Props {
    children: JSX.Element;
}

export const AuthContext = createContext<AuthContextModel>({
    isAuthenticated: false,
    setIsAuthenticated: function (): void {},
    login: function (): void {},
    logout: function (): void {},
    setUser: function (): void {},
    refreshToken: '',
    refreshAuthFromToken: function (): void {},
    hash: undefined,
    setHash: function (): void {},
    redirect: undefined,
    setRedirect: function (): void {},
    clientId: undefined,
    setClientId: function(): void {},
    clearRefreshToken: function(): void {},
});

const AuthenticationContextProvider = (props: Props) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<CurrentUserModel>();
    const [cookies, setCookie, removeCookie] = useCookies(['refreshToken', refreshRedeemGuardCookie, 'sHSSOEXTChallenge', 'sHSSORedirect']);
    const [clientId, setClientId] = useState<string | undefined>();

    const queryClient = useQueryClient();

    const getRefreshRedeemGuardKey = (incomingClientId?: string) => incomingClientId || '__default__';

    const parseRefreshRedeemGuard = (rawValue: unknown): RefreshRedeemGuardState | undefined => {
        if (!rawValue || typeof rawValue !== 'string') return undefined;

        try {
            const decodedValue = decodeURIComponent(rawValue);
            const parsed = JSON.parse(decodedValue) as Partial<RefreshRedeemGuardState>;
            if (!parsed?.data || typeof parsed.data !== 'object' || Array.isArray(parsed.data)) {
                return undefined;
            }

            const normalizedData: Record<string, RefreshRedeemGuardClientData> = {};
            Object.entries(parsed.data).forEach(([key, value]) => {
                const candidate = value as Partial<RefreshRedeemGuardClientData>;
                if (typeof candidate?.count === 'number' && typeof candidate?.expiresAt === 'number') {
                    normalizedData[key] = {
                        count: candidate.count,
                        expiresAt: candidate.expiresAt,
                    };
                }
            });

            if (Object.keys(normalizedData).length > 0) {
                return { data: normalizedData };
            }
        } catch (error) {
            return undefined;
        }

        return undefined;
    };

    const clearRefreshRedeemGuardCookie = async (incomingClientId?: string) => {
        if (!incomingClientId) {
            removeCookie(refreshRedeemGuardCookie, {
                path: '/',
                domain: window.location.hostname,
            });
            return;
        }

        const guardCookie = await cookieStore.get(refreshRedeemGuardCookie);
        const parsedState = parseRefreshRedeemGuard(guardCookie?.value);

        if (!parsedState) {
            return;
        }

        const clientKey = getRefreshRedeemGuardKey(incomingClientId);
        if (!parsedState.data[clientKey]) {
            return;
        }

        const { [clientKey]: _, ...rest } = parsedState.data;

        if (Object.keys(rest).length === 0) {
            removeCookie(refreshRedeemGuardCookie, {
                path: '/',
                domain: window.location.hostname,
            });
            return;
        }

        setRefreshRedeemGuardCookie({ data: rest });
    };

    const setRefreshRedeemGuardCookie = (state: RefreshRedeemGuardState) => {
        const latestExpiry = Math.max(...Object.values(state.data).map((entry) => entry.expiresAt));

        removeCookie(refreshRedeemGuardCookie, {
            path: '/',
            domain: window.location.hostname,
        });

        setCookie(refreshRedeemGuardCookie, JSON.stringify(state), {
            path: '/',
            expires: new Date(latestExpiry),
            secure: true,
            domain: window.location.hostname,
        });
    };

    const clearRefreshCookies = () => {
        removeCookie('refreshToken', {
            path: '/',
            domain: window.location.hostname,
        });
        clearRefreshRedeemGuardCookie();
    };

    const incrementRefreshRedeemCount = async (clientId?: string) => {
        const now = Date.now();
        const guardCookie = await cookieStore.get(refreshRedeemGuardCookie);
        const parsedState = parseRefreshRedeemGuard(guardCookie?.value) || { data: {} };
        const clientKey = getRefreshRedeemGuardKey(clientId);
        const currentGuard = parsedState.data[clientKey];
        
        const activeWindow = currentGuard && currentGuard.expiresAt > now;
        const expiresAt = activeWindow ? currentGuard.expiresAt : now + refreshRedeemWindowMs;
        const count = activeWindow ? currentGuard.count + 1 : 1;
        if (count >= refreshRedeemLimit) {
            clearRefreshCookies();
            return true;
        }

        setRefreshRedeemGuardCookie({
            data: {
                ...parsedState.data,
                [clientKey]: { count, expiresAt },
            },
        });
        return false;
    };

      /**
     * request new JWT + Refresh token using refresh token in cookie
     * @param param0 
     */
    const doRefresh = async ({ callback, onError, payload, clientId }: AuthVariablesType) => {
        const rTCookie = await cookieStore.get('refreshToken');
        await axios.post(
            `${process.env.REACT_APP_API_ROOT}/V1/Auth/${rTCookie?.value}`, payload
        ).then(async response => {
            const user = response.data;

            if (await incrementRefreshRedeemCount(clientId)) {
                logout('Refresh token loop detected');
                onError?.(new Error('Too many refresh token redemptions detected in 1 minute.'));
                return;
            }

            setRefreshTokenCookie(user.refreshToken); // save the new token
            callback?.(response);
        }).catch(error => {
            onError?.(error);
        });
    }

    /**
     * attempt to exchange refresh token in cookie for new JWT + refresh token
     */
    const refreshAuthFromToken = async ({ callback, onError, payload, clientId }: AuthVariablesType) => {
        const refreshTokenLock = window.location.hostname + '_getNewRefreshToken';
        await navigator.locks.request(
            refreshTokenLock, // exclusive access, so other tabs will need to wait
            { mode: "exclusive" }, // once lock is acquired, run the async function
            async (lock) => {
                if (lock) {
                    await doRefresh({ callback, onError, payload, clientId });
                }
            },
        );
    };

    const setUserInfo = (userInfo: AuthResponseModel) => {
        const newUser = {
            id: userInfo.id,
            twoFactorRequired: userInfo.twoFactorRequired,
            twoFactorViaEmail: userInfo.twoFactorViaEmail,
            email: userInfo.email,
            token: userInfo.token,
            refreshToken: userInfo.refreshToken,
            expires: userInfo.expires,
        };

        setUser(newUser);
    };

    const login = (user: AuthResponseModel, clearCookie = true) => {
        resetLocalStorage();

        // set cookie and start countdown to silent refresh
        setRefreshTokenCookie(user.refreshToken);
        if (clearCookie) clearRefreshRedeemGuardCookie();
    };

    const logout = (reason = '') => {
        clearRefreshCookies();
        setIsAuthenticated(false);
        setUser(undefined);
        resetLocalStorage();
        inMemoryJWT.eraseToken();
        queryClient.removeQueries();
    };

    const resetLocalStorage = () => {
        const backup: GenericStringObject = {};
        localStorageKeys.forEach((k) => {
            const value = localStorage.getItem(k);
            if (value) backup[k] = value;
        });

        localStorage.clear();

        localStorageKeys.forEach((k) => {
            if (backup[k]) localStorage.setItem(k, backup[k]);
        });
    };

    const setRefreshTokenCookie = (refreshToken: string) => {
        const expires = new Date();
        expires.setHours(new Date().getHours() + 24);

        setCookie('refreshToken', refreshToken, {
            path: '/',
            expires,
            secure: true,
            domain: window.location.hostname,
        });
    };

    const handleHash = (value: string | undefined) => {
        const expires = new Date();
        expires.setMinutes(new Date().getMinutes() + 30);
        setCookie('sHSSOEXTChallenge', value, {
            path: '/',
            expires,
            secure: true,
            domain: window.location.hostname,
            // httpOnly: true
        });
    };

    const handleRedirect = (value: string | undefined) => {
        const expires = new Date();
        expires.setMinutes(new Date().getMinutes() + 30);
        setCookie('sHSSORedirect', value, {
            path: '/',
            expires,
            secure: true,
            domain: window.location.hostname,
            // httpOnly: true
        });
        
    }

    axios.interceptors.request.use(
        function (config: any) {
            const { origin } = new URL(config.url ?? '');
            const allowedOrigins = process.env.REACT_APP_API_ROOT;
            const token = inMemoryJWT.getToken();

            if (allowedOrigins?.includes(origin)) {
                const _headers = config.headers ?? {};
                config.headers = {
                    ..._headers,
                    authorization: `Bearer ${token}`,
                };
            }
            if (config.method === 'delete' && !config.data) {
                config.data = { delete: true };
            } else if (config.method === 'post' && !config.data) {
                config.data = {};
            }

            return config;
        },
        function (error) {
            return Promise.reject(error);
        }
    );

    axios.interceptors.response.use(
        (response: AxiosResponse<any, any>) => {
            return response;
        },
        (err: AxiosError) => {
            return new Promise((resolve, reject) => {
                if (err?.response?.status === 401) {
                    if (isAuthenticated) logout('Unauthorized');
                }
                return reject(err);
            });
        }
    );

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated: isAuthenticated,
                setIsAuthenticated: setIsAuthenticated,
                login,
                logout,
                user,
                setUser: setUserInfo,
                refreshToken: cookies['refreshToken'],
                clearRefreshToken: clearRefreshCookies,
                refreshAuthFromToken,
                setHash: handleHash,
                hash: cookies['sHSSOEXTChallenge'],
                setRedirect: handleRedirect,
                redirect: cookies['sHSSORedirect'],
                clientId,
                setClientId
            }}
        >
            {props.children}
        </AuthContext.Provider>
    );
};

export default AuthenticationContextProvider;
